import React from 'react';
import Icon, { IconNames } from 'components/Icon';
import {
  caseInsensitiveSort,
  capitalizeUpperText,
  formatNumberAsCurrency,
  parseCurrency,
  safeHref,
} from 'helpers/utils';
import { ENV_CONFIG } from 'config/environment';
import colors from './colors';

// Page-preview links (webUrl) must point at our own donation-page domain — app.oath.vote in
// prod, test.oath.vote otherwise — so a link cell can never resolve to an unexpected host.
const pageCfg = ENV_CONFIG();
const ALLOWED_PAGE_HOSTS = [
  ...new Set(
    [pageCfg.PARTNER_PAGE, pageCfg.EVENT_PAGE, pageCfg.COMMUNITY_PAGE].map(u => new URL(u).hostname)
  ),
];

const renderStatusBadge = (status: string) => (
  <div className="page-status-container">
    <div className={`page-status ${status.toLowerCase()}`}>
      <Icon
        name={status === 'ACTIVE' ? IconNames.Check : IconNames.Pause}
        color={colors.white}
        size={9}
      />
    </div>
    <span>{capitalizeUpperText(status)}</span>
  </div>
);

export const PENDING_TRANSFER_LABEL = 'Pending';

const renderAmountOrPending = (amount: string) =>
  amount === PENDING_TRANSFER_LABEL ? amount : formatNumberAsCurrency(Number(amount));

// parseCurrency reads 'Pending' as 0, which would interleave Pending rows with $0 rows.
const compareAmountOrPending = (a: string, b: string) => {
  const aIsPending = a === PENDING_TRANSFER_LABEL;
  const bIsPending = b === PENDING_TRANSFER_LABEL;
  if (aIsPending && bIsPending) return 0;
  if (aIsPending) return -1;
  if (bIsPending) return 1;
  return parseCurrency(a) - parseCurrency(b);
};

const renderArrayWithLineBreaks = (value: string | string[] | undefined) => {
  if (Array.isArray(value)) {
    return (
      <>
        {value.map((item, index) => (
          <span key={index}>
            {item}
            {index < value.length - 1 && <br />}
          </span>
        ))}
      </>
    );
  }
  return value || '';
};

const renderWebUrlCell = (item: any, handleCopy?: (text: string) => void) => {
  const href = safeHref(item.webUrl, ALLOWED_PAGE_HOSTS);
  return (
    <div className="url-cell">
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="link-text url-cell__link"
        >
          {item.webUrl}
          <span className="sr-only"> (opens in new tab)</span>
        </a>
      ) : (
        <span>{item.webUrl}</span>
      )}
      <button
        type="button"
        className="url-cell__copy-button"
        onClick={() => handleCopy?.(item.webUrl)}
        aria-label={`Copy URL ${item.webUrl} to clipboard`}
      >
        <span aria-hidden="true">
          <Icon name={IconNames.Copy} color={colors.oathBlue} />
        </span>
      </button>
    </div>
  );
};

type SortKey = keyof typeof sortFns;

export interface ITableColumn {
  label: string;
  accessor: string;
  visible: boolean;
  sort?: { sortKey: SortKey };
  renderCell?: (item: any, handleCopy?: (text: string) => void) => React.ReactNode;
}

export const summaryRecipientColumns = [
  'Recipient Name',
  // 'Status',
  // 'Impact Score',
  'Amount Raised',
  '# of Donations',
];

export const summaryPageColumns = [
  'Page Name',
  'Amount Donated',
  '# of Donations',
  // 'Status',
  // 'Latest Donation',
];

export const eventPageColumns: ITableColumn[] = [
  {
    label: 'Updated Date',
    accessor: 'updatedDate',
    visible: true,
    sort: { sortKey: 'updatedDate' },
    renderCell: item => item.updatedDate,
  },
  {
    label: 'Page Name',
    accessor: 'pageName',
    visible: true,
    sort: { sortKey: 'pageName' },
    renderCell: item => item.pageName,
  },
  {
    label: 'Website URL',
    accessor: 'webUrl',
    visible: true,
    sort: { sortKey: 'webUrl' },
    renderCell: renderWebUrlCell,
  },
  {
    label: 'Event Date',
    accessor: 'eventDate',
    visible: true,
    sort: { sortKey: 'eventDate' },
    renderCell: item => item.eventDate,
  },
  {
    label: 'Page Status',
    accessor: 'status',
    visible: true,
    sort: { sortKey: 'status' },
    renderCell: item => renderStatusBadge(item.status),
  },
  {
    label: 'Actions',
    accessor: 'actions',
    visible: true,
    renderCell: item => item.actions,
  },
];

export const donatePageColumns: ITableColumn[] = [
  {
    label: 'Updated Date',
    accessor: 'updatedDate',
    visible: true,
    sort: { sortKey: 'updatedDate' },
    renderCell: item => item.updatedDate,
  },
  {
    label: 'Page Name',
    accessor: 'pageName',
    visible: true,
    sort: { sortKey: 'pageName' },
    renderCell: item => item.pageName,
  },
  {
    label: 'Website URL',
    accessor: 'webUrl',
    visible: true,
    sort: { sortKey: 'webUrl' },
    renderCell: renderWebUrlCell,
  },
  {
    label: 'Page Status',
    accessor: 'status',
    visible: true,
    sort: { sortKey: 'status' },
    renderCell: item => renderStatusBadge(item.status),
  },
  {
    label: 'Actions',
    accessor: 'actions',
    visible: true,
    renderCell: item => item.actions,
  },
];

export const communityPageColumns: ITableColumn[] = [
  {
    label: 'Page ID',
    accessor: 'querystring',
    visible: true,
    sort: { sortKey: 'querystring' },
    renderCell: item => item.querystring,
  },
  {
    label: 'Title',
    accessor: 'set_title',
    visible: true,
    sort: { sortKey: 'set_title' },
    renderCell: item => item.set_title,
  },
  {
    label: 'Website URL',
    accessor: 'webUrl',
    visible: true,
    sort: { sortKey: 'webUrl' },
    renderCell: renderWebUrlCell,
  },
  {
    label: 'Actions',
    accessor: 'actions',
    visible: true,
    renderCell: item => item.actions,
  },
];

export const partnerDonorColumns: ITableColumn[] = [
  {
    label: 'Donation Date',
    accessor: 'date',
    visible: true,
    sort: { sortKey: 'date' },
    renderCell: item => item.date,
  },
  {
    label: 'Donor Name',
    accessor: 'name',
    visible: true,
    sort: { sortKey: 'name' },
    renderCell: item => item.name,
  },
  {
    label: 'Amount Donated',
    accessor: 'amount',
    visible: true,
    sort: { sortKey: 'amount' },
    renderCell: item => formatNumberAsCurrency(item.amount),
  },
  {
    label: 'Page Name',
    accessor: 'page_name',
    visible: false,
    sort: { sortKey: 'pageName' },
    renderCell: item => item.page_name,
  },
  {
    label: 'Page URL',
    accessor: 'page_url',
    visible: true,
    sort: { sortKey: 'pageUrl' },
    renderCell: item => item.page_url,
  },
  {
    label: 'Reference Code',
    accessor: 'ref',
    visible: true,
    sort: { sortKey: 'ref' },
    renderCell: item => item.ref,
  },
  {
    label: 'Email',
    accessor: 'email',
    visible: false,
    sort: { sortKey: 'email' },
    renderCell: item => item.email,
  },
  {
    label: 'City',
    accessor: 'city',
    visible: false,
    sort: { sortKey: 'city' },
    renderCell: item => item.city,
  },
  {
    label: 'State',
    accessor: 'state',
    visible: false,
    sort: { sortKey: 'state' },
    renderCell: item => item.state,
  },
  {
    label: 'Transaction ID',
    accessor: 'transaction_id',
    visible: false,
    sort: { sortKey: 'transaction_id' },
    renderCell: item => item.transaction_id,
  },
  {
    label: 'Payment Method',
    accessor: 'payment_method',
    visible: false,
    sort: { sortKey: 'payment_method' },
    renderCell: item => item.payment_method,
  },
  {
    label: 'Recurring',
    accessor: 'is_recurring_donation',
    visible: false,
    sort: { sortKey: 'is_recurring_donation' },
    renderCell: item => item.is_recurring_donation,
  },
];

const employerAddressColumns: ITableColumn[] = [
  {
    label: 'Employer City',
    accessor: 'employer_city',
    visible: false,
    sort: { sortKey: 'employer_city' },
    renderCell: item => item.employer_city,
  },
  {
    label: 'Employer State',
    accessor: 'employer_state',
    visible: false,
    sort: { sortKey: 'employer_state' },
    renderCell: item => item.employer_state,
  },
  {
    label: 'Employer Zip',
    accessor: 'employer_zip',
    visible: false,
    sort: { sortKey: 'employer_zip' },
    renderCell: item => item.employer_zip,
  },
  {
    label: 'Employer Address',
    accessor: 'employer_address',
    visible: false,
    sort: { sortKey: 'employer_address' },
    renderCell: item => item.employer_address,
  },
  {
    label: 'Employer Address 2',
    accessor: 'employer_address_2',
    visible: false,
    sort: { sortKey: 'employer_address_2' },
    renderCell: item => item.employer_address_2,
  },
];

export const getRecipientDonorColumns = (isEmployerAddressRequired: boolean): ITableColumn[] => [
  {
    label: 'Donation Date',
    accessor: 'date',
    visible: true,
    sort: { sortKey: 'date' },
    renderCell: item => item.date,
  },
  {
    label: 'Donor Name',
    accessor: 'name',
    visible: true,
    sort: { sortKey: 'name' },
    renderCell: item => item.name,
  },
  {
    label: 'Original Donation Amount',
    accessor: 'original_amount',
    visible: false,
    sort: { sortKey: 'original_amount' },
    renderCell: item => formatNumberAsCurrency(item.original_amount),
  },
  {
    label: 'Amount Donated',
    accessor: 'amount',
    visible: true,
    sort: { sortKey: 'amount' },
    renderCell: item => formatNumberAsCurrency(item.amount),
  },
  {
    label: 'Processing Fee',
    accessor: 'processing_fee',
    visible: true,
    sort: { sortKey: 'processing_fee' },
    renderCell: item => renderAmountOrPending(item.processing_fee),
  },
  {
    label: 'Total Disbursement',
    accessor: 'disbursement_amount',
    visible: true,
    sort: { sortKey: 'disbursement_amount' },
    renderCell: item => renderAmountOrPending(item.disbursement_amount),
  },
  {
    label: 'Address',
    accessor: 'address',
    visible: false,
    sort: { sortKey: 'address' },
    renderCell: item => item.address,
  },
  {
    label: 'Address 2',
    accessor: 'address_2',
    visible: false,
    sort: { sortKey: 'address_2' },
    renderCell: item => item.address_2,
  },
  {
    label: 'City',
    accessor: 'city',
    visible: false,
    sort: { sortKey: 'city' },
    renderCell: item => item.city,
  },
  {
    label: 'State',
    accessor: 'state',
    visible: false,
    sort: { sortKey: 'state' },
    renderCell: item => item.state,
  },
  {
    label: 'Zip',
    accessor: 'zip',
    visible: false,
    sort: { sortKey: 'zip' },
    renderCell: item => item.zip,
  },
  {
    label: 'Employer',
    accessor: 'employer',
    visible: false,
    sort: { sortKey: 'employer' },
    renderCell: item => item.employer,
  },
  {
    label: 'Occupation',
    accessor: 'occupation',
    visible: false,
    sort: { sortKey: 'occupation' },
    renderCell: item => item.occupation,
  },
  ...(isEmployerAddressRequired ? employerAddressColumns : []),
  {
    label: 'Payment Method',
    accessor: 'payment_method',
    visible: false,
    sort: { sortKey: 'payment_method' },
    renderCell: item => item.payment_method,
  },
];

export const recipientColumns: ITableColumn[] = [
  {
    label: 'Recipient Name',
    accessor: 'name',
    visible: true,
    sort: { sortKey: 'name' },
    renderCell: item => item.name,
  },
  {
    label: 'Subtype',
    accessor: 'subtype',
    visible: true,
    sort: { sortKey: 'subtype' },
    renderCell: item => item.subtype,
  },
  {
    label: 'Stripe Status',
    accessor: 'stripe_status',
    visible: true,
    sort: { sortKey: 'stripe_status' },
    renderCell: item => item.stripe_status,
  },
  {
    label: 'Stripe Authorized',
    accessor: 'is_stripe_authorized',
    visible: true,
    sort: { sortKey: 'is_stripe_authorized' },
    renderCell: item => item.is_stripe_authorized,
  },
  {
    label: 'Onboarding Form Complete',
    accessor: 'onboarding_form_complete',
    visible: true,
    sort: { sortKey: 'onboarding_form_complete' },
    renderCell: item => item.onboarding_form_complete,
  },
  {
    label: 'Stripe ID',
    accessor: 'stripe_id',
    visible: true,
    sort: { sortKey: 'stripe_id' },
    renderCell: item => item.stripe_id,
  },
  {
    label: 'DE Status',
    accessor: 'de_status',
    visible: true,
    sort: { sortKey: 'de_status' },
    renderCell: item => item.de_status,
  },
  {
    label: 'DE ID',
    accessor: 'de_id',
    visible: true,
    sort: { sortKey: 'de_id' },
    renderCell: item => item.de_id,
  },
  { label: 'Actions', accessor: 'actions', visible: true, renderCell: item => item.actions },
];

export const adminColumns: ITableColumn[] = [
  {
    label: 'Admin Name',
    accessor: 'name',
    visible: true,
    sort: { sortKey: 'name' },
    renderCell: item => item.name,
  },
  {
    label: 'Admin Email',
    accessor: 'email',
    visible: true,
    sort: { sortKey: 'email' },
    renderCell: item => item.email,
  },
  {
    label: 'Role Types',
    accessor: 'roleTypes',
    visible: true,
    sort: { sortKey: 'roleTypes' },
    renderCell: item => renderArrayWithLineBreaks(item.roleTypes),
  },
  {
    label: 'Client/Recipient Name',
    accessor: 'clientName',
    visible: true,
    sort: { sortKey: 'clientName' },
    renderCell: item => renderArrayWithLineBreaks(item.clientName),
  },
  { label: 'Actions', accessor: 'actions', visible: true, renderCell: item => item.actions },
];

const payoutsTypeColumn = {
  label: 'Type',
  accessor: 'type_label',
  visible: true,
  renderCell: item => item.type_label,
};

export const payoutsColumns: ITableColumn[] = [
  {
    label: 'Date',
    accessor: 'date',
    visible: true,
    sort: { sortKey: 'date' },
    renderCell: item => item.date,
  },
  { label: 'Actions', accessor: 'actions', visible: true, renderCell: item => item.actions },
];

export const payoutsAllTabColumns = [payoutsTypeColumn, ...payoutsColumns];

export const recipientAdminColumns: ITableColumn[] = [
  {
    label: 'Name',
    accessor: 'name',
    visible: true,
    sort: { sortKey: 'name' },
    renderCell: item => item.name,
  },
  {
    label: 'Email',
    accessor: 'email',
    visible: true,
    sort: { sortKey: 'email' },
    renderCell: item => item.email,
  },
  {
    label: 'Position',
    accessor: 'positionCategory',
    visible: true,
    renderCell: item => item.positionCategory,
  },
  {
    label: 'Terms Accepted',
    accessor: 'tosAcceptedLabel',
    visible: true,
    renderCell: item => item.tosAcceptedLabel,
  },
  {
    label: 'Status',
    accessor: 'statusLabel',
    visible: true,
    renderCell: item => item.statusLabel,
  },
  {
    label: 'Added',
    accessor: 'createdLabel',
    visible: true,
    renderCell: item => item.createdLabel,
  },
  { label: 'Actions', accessor: 'actions', visible: true, renderCell: item => item.actions },
];

export const buttonTypes = {
  edit: { icon: IconNames.Edit },
  delete: { icon: IconNames.Trash },
  preview: { icon: IconNames.Eye },
  duplicate: { icon: IconNames.Copy },
  view: { icon: IconNames.Eye },
  archive: { icon: IconNames.PauseCircle },
  restore: { icon: IconNames.PlayCircle },
};

export const sortFns = {
  updatedDate: array => [...array].sort((a, b) => a.updatedDate - b.updatedDate),
  pageName: array => [...array].sort((a, b) => caseInsensitiveSort(a.page_name, b.page_name)),
  pageUrl: array => [...array].sort((a, b) => caseInsensitiveSort(a.page_url, b.page_url)),
  webUrl: array => [...array].sort((a, b) => caseInsensitiveSort(a.webUrl, b.webUrl)),
  eventDate: array => [...array].sort((a, b) => a.eventDate - b.eventDate),
  name: array => [...array].sort((a, b) => caseInsensitiveSort(a.name, b.name)),
  subtype: array => [...array].sort((a, b) => caseInsensitiveSort(a.subtype, b.subtype)),
  date: array => [...array].sort((a, b) => a.date - b.date),
  amount: array => [...array].sort((a, b) => parseCurrency(a.amount) - parseCurrency(b.amount)),
  original_amount: array =>
    [...array].sort((a, b) => parseCurrency(a.original_amount) - parseCurrency(b.original_amount)),
  processing_fee: array =>
    [...array].sort((a, b) => compareAmountOrPending(a.processing_fee, b.processing_fee)),
  disbursement_amount: array =>
    [...array].sort((a, b) => compareAmountOrPending(a.disbursement_amount, b.disbursement_amount)),
  email: array => [...array].sort((a, b) => caseInsensitiveSort(a.email, b.email)),
  address: array => [...array].sort((a, b) => caseInsensitiveSort(a.address, b.address)),
  address_2: array => [...array].sort((a, b) => caseInsensitiveSort(a.address_2, b.address_2)),
  city: array => [...array].sort((a, b) => caseInsensitiveSort(a.city, b.city)),
  state: array => [...array].sort((a, b) => caseInsensitiveSort(a.state, b.state)),
  zip: array => [...array].sort((a, b) => caseInsensitiveSort(a.zip, b.zip)),
  employer: array => [...array].sort((a, b) => caseInsensitiveSort(a.employer, b.employer)),
  occupation: array => [...array].sort((a, b) => caseInsensitiveSort(a.occupation, b.occupation)),
  employer_address: array =>
    [...array].sort((a, b) => caseInsensitiveSort(a.employer_address, b.employer_address)),
  employer_address_2: array =>
    [...array].sort((a, b) => caseInsensitiveSort(a.employer_address_2, b.employer_address_2)),
  employer_city: array =>
    [...array].sort((a, b) => caseInsensitiveSort(a.employer_city, b.employer_city)),
  employer_state: array =>
    [...array].sort((a, b) => caseInsensitiveSort(a.employer_state, b.employer_state)),
  employer_zip: array =>
    [...array].sort((a, b) => caseInsensitiveSort(a.employer_zip, b.employer_zip)),
  transaction_id: array =>
    [...array].sort((a, b) => caseInsensitiveSort(a.transaction_id, b.transaction_id)),
  clientName: array =>
    [...array].sort((a, b) => {
      const normalize = (v: string | string[]) => (Array.isArray(v) ? v.join(', ') : (v ?? ''));
      return caseInsensitiveSort(normalize(a.clientName), normalize(b.clientName));
    }),
  is_stripe_authorized: array =>
    [...array].sort((a, b) =>
      a.is_stripe_authorized === b.is_stripe_authorized ? 0 : a.is_stripe_authorized ? 1 : -1
    ),
  onboarding_form_complete: array =>
    [...array].sort((a, b) =>
      a.onboarding_form_complete === b.onboarding_form_complete
        ? 0
        : a.onboarding_form_complete
          ? 1
          : -1
    ),
  is_recurring_donation: array =>
    [...array].sort((a, b) =>
      caseInsensitiveSort(a.is_recurring_donation, b.is_recurring_donation)
    ),
  ref: array => [...array].sort((a, b) => caseInsensitiveSort(a.ref, b.ref)),
  payment_method: array =>
    [...array].sort((a, b) => caseInsensitiveSort(a.payment_method, b.payment_method)),
  status: array => [...array].sort((a, b) => caseInsensitiveSort(a.status, b.status)),
  querystring: array =>
    [...array].sort((a, b) => caseInsensitiveSort(a.querystring, b.querystring)),
  set_title: array => [...array].sort((a, b) => caseInsensitiveSort(a.set_title, b.set_title)),
  stripe_status: array =>
    [...array].sort((a, b) => caseInsensitiveSort(a.stripe_status, b.stripe_status)),
  stripe_id: array => [...array].sort((a, b) => caseInsensitiveSort(a.stripe_id, b.stripe_id)),
  de_status: array => [...array].sort((a, b) => caseInsensitiveSort(a.de_status, b.de_status)),
  de_id: array => [...array].sort((a, b) => caseInsensitiveSort(a.de_id, b.de_id)),
  roleTypes: array =>
    [...array].sort((a, b) => {
      const normalize = (v: string | string[]) => (Array.isArray(v) ? v.join(', ') : (v ?? ''));
      return caseInsensitiveSort(normalize(a.roleTypes), normalize(b.roleTypes));
    }),
};
